"use client";

import { useEffect, useRef, useState } from "react";
import type React from "react";
import Image from "next/image";
import type { StaticImageData } from "next/image";
import Link from "next/link";
import { stringDateFormat } from "@/lib/Utility";
import DOMPurify from "isomorphic-dompurify";

export type BlogSlide = {
  imageSrc: string | StaticImageData;
  categories: { name: string; slug: string }[];
  title: string;
  date: string;
  href?: string;
  isLatest?: boolean;
};

type BlogHeroSliderProps = {
  slides: BlogSlide[];
  sliderId?: string; // for unique nav/pagination hooks
};

export default function BlogHeroSlider({
  slides,
  sliderId = "blog-hero",
}: BlogHeroSliderProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pointerIdRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragDeltaXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const dragStartTimeRef = useRef(0);
  const [rendered, setRendered] = useState<BlogSlide[]>(() =>
    slides?.length ? [slides[slides.length - 1], ...slides, slides[0]] : [],
  );

  const [activeIndex, setActiveIndex] = useState(1); // points at original first item
  const [isInitialized, setIsInitialized] = useState(false);

  // Update rendered array when slides prop changes
  useEffect(() => {
    if (slides?.length) {
      setRendered([slides[slides.length - 1], ...slides, slides[0]]);
      setActiveIndex(1);
      setIsInitialized(false);
    }
  }, [slides]);

  // Initialize: scroll to center without animation on first mount
  useEffect(() => {
    if (
      !isInitialized &&
      itemRefs.current[activeIndex] &&
      rendered.length > 0
    ) {
      // Use instant scroll on initialization
      itemRefs.current[activeIndex]?.scrollIntoView({
        behavior: "instant" as ScrollBehavior,
        inline: "center",
        block: "nearest",
      });
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        setIsInitialized(true);
      }, 100);
    }
  }, [activeIndex, isInitialized, rendered.length]);

  // Smooth scroll on subsequent changes
  useEffect(() => {
    if (isInitialized && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeIndex, isInitialized]);

  // The /latest-three-blogs endpoint returns WordPress thumbnail URLs
  // (`name-<W>x<H>.ext`, e.g. a 300x144 crop) which upscale to a blurry mess in
  // this ~800px-wide slide. Strip the `-WxH` suffix to load the full-size
  // original instead. Only applies to string URLs (not StaticImageData).
  const normalizeImageSrc = (src: string | StaticImageData) =>
    typeof src === "string"
      ? src.replace(/-\d+x\d+(?=\.[a-zA-Z0-9]+$)/, "")
      : src;

  const total = slides.length;
  const displayIndex = ((activeIndex - 1 + total) % total) + 1;

  const handleNext = () => {
    if (activeIndex === rendered.length - 2) {
      setRendered((prev) => [...prev, ...slides]);
    }
    setActiveIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (activeIndex === 1) {
      setRendered((prev) => {
        const nextArr = [...slides, ...prev];
        // shift active to keep same visual item centered
        setActiveIndex((idx) => idx + slides.length);
        return nextArr;
      });
    } else {
      setActiveIndex((prev) => prev - 1);
    }
  };

  // Pointer/Touch drag support (swipe to navigate)
  const DRAG_THRESHOLD_PX = 50;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only react to primary button on mouse, allow touch/pen
    if (e.pointerType === "mouse" && e.buttons !== 1) return;
    isDraggingRef.current = true;
    pointerIdRef.current = e.pointerId;
    dragStartXRef.current = e.clientX;
    didDragRef.current = false;
    dragDeltaXRef.current = 0;
    dragStartTimeRef.current = performance.now();
    dragStartScrollLeftRef.current = viewportRef.current?.scrollLeft ?? 0;
    // (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || pointerIdRef.current !== e.pointerId) return;
    const dx = e.clientX - dragStartXRef.current;
    dragDeltaXRef.current = dx;

    if (Math.abs(dx) > 5) {
      didDragRef.current = true;
    }
    // Provide real-time drag feel by moving the viewport scrollLeft
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = dragStartScrollLeftRef.current - dx;
    }
  };

  const endDrag = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    const dx = dragDeltaXRef.current;
    const dt = Math.max(1, performance.now() - dragStartTimeRef.current);
    const velocity = Math.abs(dx) / dt; // px per ms
    // Decide navigation based on distance or velocity
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || velocity > 0.6) {
      if (dx < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    } else {
      // Snap back to current active if below threshold
      itemRefs.current[activeIndex]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
    pointerIdRef.current = null;
    dragDeltaXRef.current = 0;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    endDrag();
  };

  const handlePointerCancel = () => {
    endDrag();
  };
  const didDragRef = useRef(false);
  return (
    <div className="w-full" dir="ltr">
      <div
        className="overflow-hidden select-none cursor-grab active:cursor-grabbing"
        ref={viewportRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerCancel}
        onPointerCancel={handlePointerCancel}
        onDragStart={(e) => e.preventDefault()}
        style={{ touchAction: "pan-y" }}
      >
        <div ref={scrollerRef} className="flex gap-[30px]">
          {rendered?.map((slide, idx) => (
            <div
              key={`${sliderId}-${idx}`}
              ref={(el: HTMLDivElement | null) => {
                itemRefs.current[idx] = el;
              }}
              className={`min-w-[100%] xl:min-w-[calc(100%_-_30%)] lg:min-w-[calc(100%_-_10%)] transition-opacity duration-300 ${idx === activeIndex ? "opacity-100" : "opacity-20"}`}
            >
              <Link
                href={
                  slide?.href?.startsWith("/")
                    ? slide?.href + "/"
                    : `/blog/${slide?.href}/`
                }
                onClick={(e) => {
                  if (didDragRef.current) {
                    e.preventDefault();
                  }
                }}
              >
                <article
                  className="w-full flex items-stretch gap-[30px] bg-white border border-[#ECF0F6] p-[30px] shadow-[0_0_0_1px_#ECF0F6_inset]"
                  aria-label={slide.title}
                >
                  {/* Image Container */}
                  <div className="relative basis-[63%] aspect-[7.8/3.7]">
                    <Image
                      src={normalizeImageSrc(slide?.imageSrc ?? "")}
                      alt={slide.title}
                      fill
                      quality={100}
                      sizes="(min-width: 1280px) 100vw"
                      className="object-contain object-center pointer-events-none"
                      priority={idx === activeIndex}
                      fetchPriority="high"
                    />
                  </div>

                  {/* FIX 2: Fixed Right Side Layout */}
                  <div className="basis-[37%] flex flex-col justify-between py-[8px]">
                    <div className="flex flex-col gap-[20px]">
                      <div className="flex flex-wrap items-center gap-3">
                        {slide.isLatest && (
                          <span className="px-[20px] py-[8px] rounded-full bg-black text-white text-[14px] xl:text-[16px] leading-tight font-medium whitespace-nowrap">
                            Latest Article
                          </span>
                        )}
                        {slide?.categories?.map((cat, catIdx) => (
                          <span
                            key={catIdx}
                            className="px-[20px] py-[8px] rounded-full border bg-[#F2F4F8] border-[#ECF0F6] text-[#00040b] text-[14px] xl:text-[16px] leading-tight font-medium whitespace-nowrap"
                          >
                            {cat?.name}
                          </span>
                        ))}
                      </div>

                      <h2 className="clash-font font-medium text-[#00040b] leading-[1.2] text-[28px] xl:text-[38px] hover:underline  underline-offset-4 decoration-2 decoration-[#00040b]">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(slide.title) || "",
                          }}
                        />
                      </h2>
                    </div>

                    <div className="text-[#00040b]/60 text-[16px] xl:text-[18px] font-medium mt-4">
                      {/* Pin en-GB so this renders identically on server and
                          client. A bare toLocaleDateString() gave "M/D/YYYY" on
                          the server (Node/en-US) and "D/M/YYYY" in the browser;
                          stringDateFormat() assumes D/M/YYYY, so the server built
                          a wrong date and the client a correct one → React #418
                          hydration mismatch. */}
                      {stringDateFormat(
                        new Date(slide.date).toLocaleDateString("en-GB"),
                      )}
                    </div>
                  </div>
                </article>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-[20px] flex items-center gap-[14px] text-white">
        <div className="flex items-center gap-[10px] min-w-[100%] xl:min-w-[calc(100%_-_30%)] lg:min-w-[calc(100%_-_10%)] mx-auto">
          <button
            onClick={handlePrev}
            aria-label="Previous slide"
            className="w-[40px] h-[40px] rounded-full bg-transparent text-[#fff] grid place-items-center border border-white cursor-pointer"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 6L9 12L15 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="text-[16px] leading-[140%] min-w-[64px] text-center">
            {String(displayIndex).padStart(2, "0")}{" "}
            <span className="opacity-50">{"//"}</span>{" "}
            {String(total).padStart(2, "0")}
          </div>
          <button
            onClick={handleNext}
            aria-label="Next slide"
            className="w-[40px] h-[40px] rounded-full bg-white text-[#00040b] 
            grid place-items-center border border-white/20 hover:bg-white/90
            cursor-pointer
            "
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 6L15 12L9 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
